import React, { useState, useEffect } from 'react'
import { Plus, Download, X, Loader2, Eye, EyeOff, IndianRupee, Layers, Hash } from 'lucide-react'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

import ExcelDataGrid from '../common/ExcelDataGrid'
import axios from 'axios'
import { useError } from '../../contexts/ErrorContext'

const ChallanDetailsTable = ({
  challanData: propChallanData,
  tdsData: propTdsData,
  originalChallanData: propOriginalChallanData,
  onChallanDataChange,
  isRevised,
  isCorrection,
  tan,
  financialYear,
  sectionOptions: selectedSectionOptions,
  minorHeadOptions: selectedMinorHeadOptions,
  is26Q
}) => {
  const [challanData, setChallanData] = useState(propChallanData || [])
  const [showOnlyWithBalance, setShowOnlyWithBalance] = useState(false)
  const { addError } = useError()

  const isCorrectionMode = isRevised || isCorrection;

  const normalizeVal = (val) => {
    if (val === null || val === undefined) return '';
    return String(val).trim();
  };

  const getOriginalRow = (row) => {
    if (!isCorrectionMode || !propOriginalChallanData || propOriginalChallanData.length === 0) return null;
    return propOriginalChallanData.find(r => (r.id && row.id && String(r.id) === String(row.id)) || (r.bsrCode === row.bsrCode && String(r.challanSerialNo) === String(row.challanSerialNo)));
  };

  const isRowNew = (row) => {
    if (!isCorrectionMode || !propOriginalChallanData || propOriginalChallanData.length === 0) return false;
    return !getOriginalRow(row);
  };

  const isRowModified = (row) => {
    const original = getOriginalRow(row);
    if (!original) return false;

    const fieldsToCheck = [
      'sectionCode', 'bsrCode', 'dateOfDeposit', 'minorHead', 'challanSerialNo',
      'tdsAmount', 'interest', 'other', 'feesAmount', 'totalAmount',
      'interestAllocation', 'feesAllocation', 'otherAllocation'
    ];

    for (const key of fieldsToCheck) {
      const rowVal = normalizeVal(row[key]);
      const origVal = normalizeVal(original[key]);
      if (rowVal !== origVal) {
        const numRow = parseFloat(rowVal);
        const numOrig = parseFloat(origVal);
        if (!isNaN(numRow) && !isNaN(numOrig)) {
          if (Math.abs(numRow - numOrig) > 0.001) return true;
        } else {
          return true;
        }
      }
    }

    return false;
  };

  const getRowClassName = (row) => {
    if (!isCorrectionMode) return '';
    if (isRowNew(row)) return 'bg-blue-100 text-blue-900 font-medium';
    if (isRowModified(row)) return 'bg-amber-100 text-amber-900 font-medium';
    return '';
  };



  // Sync prop data
  useEffect(() => {
    if (propChallanData) {
      const calculatedData = propChallanData.map((row) => enrichRowData(row, propTdsData || []))
      setChallanData(calculatedData)
    }
  }, [propChallanData, propTdsData])

  // Recalculate used/balance when TDS data changes
  useEffect(() => {
    setChallanData(prevData => (prevData || []).map((row) => enrichRowData(row, propTdsData || [])))
  }, [propTdsData])

  // Import Modal State
  const [showImportModal, setShowImportModal] = useState(false)
  const [isHeadless, setIsHeadless] = useState(false)
  const [importStatus, setImportStatus] = useState('')
  const [importPassword, setImportPassword] = useState('')
  const [showImportPassword, setShowImportPassword] = useState(false)
  const [importDateRange, setImportDateRange] = useState({ from: '', to: '' })
  const [isImporting, setIsImporting] = useState(false)

  const handleImportClick = () => {
    if (!tan) {
      toast.error('TAN not found in profile. Please check Profile Details.')
      return
    }
    // Set default dates if empty? Maybe current FY start/end?
    // Let's leave empty for user input.
    setShowImportModal(true)
  }

  const handleImportSubmit = async (e) => {
    e.preventDefault()
    if (!importPassword) {
      toast.error('Password is required')
      return
    }
    if (!importDateRange.from || !importDateRange.to) {
      toast.error('Both Date From and Date To are required')
      return
    }

    setIsImporting(true)
    setImportStatus('Initializing...')
    const requestId = Date.now().toString()

    // Helper functions for mapping imported data
    const mapAndPopulateData = (rawData) => {
      const parseAmount = (str) => {
        if (!str || str === 'Not Found') return 0;
        const cleanStr = str.toString().replace(/,/g, '').replace(/[^0-9.]/g, '');
        return parseFloat(cleanStr) || 0;
      }

      const parseDate = (dateStr) => {
        if (!dateStr || dateStr === 'Not Found') return '';
        const str = dateStr.trim();

        if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;

        const monthMap = {
          jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06',
          jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12'
        };

        const dmmmyyyy = str.match(/^(\d{1,2})[-/\s]([A-Za-z]{3})[-/\s](\d{4})$/);
        if (dmmmyyyy) {
          const day = dmmmyyyy[1].padStart(2, '0');
          const month = monthMap[dmmmyyyy[2].toLowerCase()];
          const year = dmmmyyyy[3];
          if (month) return `${year}-${month}-${day}`;
        }

        const ddmmyyyy = str.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
        if (ddmmyyyy) {
          const day = ddmmyyyy[1].padStart(2, '0');
          const month = ddmmyyyy[2].padStart(2, '0');
          const year = ddmmyyyy[3];
          return `${year}-${month}-${day}`;
        }

        const d = new Date(str);
        if (!isNaN(d.getTime())) {
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }

        return '';
      }

      const parseSection = (natureStr) => {
        if (!natureStr || natureStr === 'Not Found') return sectionCodes[0]?.value || '';
        const upperNature = natureStr.toUpperCase();

        const directMatch = sectionCodes.find(s => upperNature.startsWith(s.value) || upperNature.includes(` ${s.value} `) || upperNature === s.value);
        if (directMatch) return directMatch.value;

        const sectionMap194To4Digit = {
          '192A': '1004', '92A': '1004',
          '194D': '1005', '94D': '1005',
          '194H': '1006', '94H': '1006',
          '194I(b)': '1007', '194I': '1007', '94I': '1007',
          '194I(a)': '1010',
          '194DA': '1011',
          '194LA': '1012',
          '193': '1019',
          '194A': '1021', '94A': '1021',
          '194C': '1023', '94C': '1023',
          '194J(a)': '1026',
          '194J(b)': '1027', '194J': '1027', '94J': '1027',
          '194': '1029',
          '194Q': '1033', '94Q': '1033',
          '194O': '1034', '94O': '1034',
          '194IA': '1035',
          '194S': '1037', '94S': '1037',
          '194B': '1058', '94B': '1058',
          '194BA': '1060',
          '194N': '1064'
        };

        const sectionMap4DigitTo194 = {
          '1004': '192A', '1005': '194D', '1006': '194H', '1007': '194I(b)',
          '1010': '194I(a)', '1011': '194DA', '1012': '194LA', '1019': '193',
          '1021': '194A', '1023': '194C', '1026': '194J(a)', '1027': '194J(b)',
          '1029': '194', '1033': '194Q', '1034': '194O', '1035': '194IA',
          '1037': '194S', '1058': '194B', '1060': '194BA', '1064': '194N'
        };

        const codeMatch = upperNature.match(/^([0-9A-Z]+)/);
        if (codeMatch) {
          let extracted = codeMatch[1];
          if (extracted.length === 3 && extracted.startsWith('9')) extracted = '1' + extracted;

          if (sectionMap194To4Digit[extracted]) {
            const targetCode = sectionMap194To4Digit[extracted];
            const matchInCodes = sectionCodes.find(s => s.value === targetCode);
            if (matchInCodes) return matchInCodes.value;
          }

          if (sectionMap4DigitTo194[extracted]) {
            const targetCode = sectionMap4DigitTo194[extracted];
            const matchInCodes = sectionCodes.find(s => s.value === targetCode);
            if (matchInCodes) return matchInCodes.value;
          }

          const fuzzyMatch = sectionCodes.find(s => s.value === extracted);
          if (fuzzyMatch) return fuzzyMatch.value;
        }

        if (upperNature.includes('CONTRACTOR')) return sectionCodes.find(s => s.value === '194C' || s.value === '1023')?.value || sectionCodes[0]?.value || '';
        if (upperNature.includes('TECHNICAL') || upperNature.includes('PROFESSIONAL') || upperNature.includes('ROYALTY')) return sectionCodes.find(s => s.value === '194J(b)' || s.value === '1027' || s.value === '1026')?.value || sectionCodes[0]?.value || '';
        if (upperNature.includes('RENT')) return sectionCodes.find(s => s.value === '194I(b)' || s.value === '1007')?.value || sectionCodes[0]?.value || '';
        if (upperNature.includes('INTEREST')) return sectionCodes.find(s => s.value === '194A' || s.value === '1021')?.value || sectionCodes[0]?.value || '';

        return sectionCodes[0]?.value || '';
      }

      const parseMinorHead = (mhStr) => {
        if (!mhStr || mhStr === 'Not Found') return '200';
        const str = mhStr.toString();
        if (str.includes('400')) return '400';
        if (str.includes('100')) return '100';
        return '200';
      }

      const newChallans = rawData.map((item, index) => {
        const cleanBsr = (item.bsrCode && item.bsrCode !== 'Not Found') ? item.bsrCode.replace(/\D/g, '') : '';
        const cleanSerial = (item.challanNumber && item.challanNumber !== 'Not Found') ? item.challanNumber.replace(/\D/g, '') : '';

        return {
          id: Date.now() + index,
          sectionCode: parseSection(item.natureOfPayment),
          bsrCode: cleanBsr,
          challanSerialNo: cleanSerial,
          dateOfDeposit: parseDate(item.dateOfPayment),
          tdsAmount: parseAmount(item.amount),
          surcharge: 0,
          educationCess: 0,
          higherEducationCess: 0,
          interest: 0,
          other: 0,
          feesAmount: 0,
          minorHead: parseMinorHead(item.minorHead),
          recordNo: '',
          bookEntry: false
        }
      })

      const enrichedNewChallans = newChallans.map(c => enrichRowData(c, propTdsData || []))

      const existingKeys = new Set(
        (challanData || []).map(c => `${c.bsrCode || ''}_${c.challanSerialNo || ''}_${c.dateOfDeposit || ''}_${c.totalAmount || c.tdsAmount || 0}`)
      );

      const uniqueNewChallans = enrichedNewChallans.filter(c => {
        const key = `${c.bsrCode || ''}_${c.challanSerialNo || ''}_${c.dateOfDeposit || ''}_${c.totalAmount || c.tdsAmount || 0}`;
        if (existingKeys.has(key)) return false;
        existingKeys.add(key);
        return true;
      });

      const updatedData = [...challanData, ...uniqueNewChallans]
      setChallanData(updatedData)
      onChallanDataChange(updatedData)
    }

    try {
      // 1. Submit job (returns immediately HTTP 200)
      const startRes = await axios.post('/api/import-challan-details', {
        credentials: {
          tan: tan,
          password: importPassword
        },
        dateRange: importDateRange,
        financialYear: financialYear,
        headless: isHeadless,
        requestId: requestId
      })

      if (!startRes.data.success) {
        toast.error('Import failed to start: ' + (startRes.data.error || 'Unknown error'))
        setIsImporting(false)
        setImportStatus('')
        return
      }

      // 2. Poll for background progress & completion
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await axios.get(`/api/import-status/${requestId}`)
          if (statusRes.data && statusRes.data.status) {
            const st = statusRes.data.status;
            if (typeof st === 'object') {
              if (st.status) setImportStatus(st.status)

              if (st.isComplete) {
                clearInterval(pollInterval)
                setIsImporting(false)
                setImportStatus('')
                setShowImportModal(false)
                setImportPassword('')

                const items = st.data || []
                toast.success(`Successfully imported ${items.length} challans!`)
                mapAndPopulateData(items)
              } else if (st.isFailed) {
                clearInterval(pollInterval)
                setIsImporting(false)
                setImportStatus('')
                toast.error('Import failed: ' + (st.error || 'Unknown error'))
              }
            } else {
              setImportStatus(String(st))
            }
          }
        } catch (err) {
          console.error('Poll Error:', err);
        }
      }, 1500)

    } catch (error) {
      console.error('Import Error:', error)
      setIsImporting(false)
      setImportStatus('')
      toast.error('Import failed: ' + (error.response?.data?.error || error.message))
    }
  }


  // Default Section Codes for 26Q
  const defaultSectionCodes = [
    { value: '192A', label: '192A - Payment of accumulated balance to employee (RPF)' },
    { value: '193', label: '193 - Interest on Securities' },
    { value: '194', label: '194 - Dividends' },
    { value: '194A', label: '194A - Interest other than "Interest on securities"' },
    { value: '194B', label: '194B - Winnings from lottery or crossword puzzle' },
    { value: '194B-P', label: '194B-P - Winnings from lottery (Kind/Cash)' },
    { value: '194BA', label: '194BA - Winnings from Online Games' },
    { value: '194BA-P', label: '194BA-P - Net Winnings from Online Games (Kind/Cash)' },
    { value: '194BB', label: '194BB - Winnings from Horse Race' },
    { value: '194C', label: '194C - Payments to Contractors/Sub-contractors' },
    { value: '194D', label: '194D - Insurance Commission' },
    { value: '194DA', label: '194DA - Payment in respect of Life Insurance Policy' },
    { value: '194EE', label: '194EE - Payments in respect of Deposit under NSS' },
    { value: '194F', label: '194F - Repurchase of units by Mutual Fund or UTI' },
    { value: '194G', label: '194G - Commission on sale of lottery tickets' },
    { value: '194H', label: '194H - Commission or brokerage' },
    { value: '194I', label: '194I - Rent (Applicable upto 2012-13)' },
    { value: '194I(a)', label: '194I(a) - Rent on Plant & Machinery' },
    { value: '194I(b)', label: '194I(b) - Rent on Land/Building/Furniture' },
    { value: '194IA', label: '194IA - TDS on sale of Property' },
    { value: '194IC', label: '194IC - Payment under Specified Agreement' },
    { value: '194J(a)', label: '194J(a) - Fees for Technical Services (@2%)' },
    { value: '194J(b)', label: '194J(b) - Fees for Professional Services (@10%)' },
    { value: '194K', label: '194K - Income from Mutual Fund Units' },
    { value: '194LA', label: '194LA - Payment of Compensation on acquisition of certain Immovable Property' },
    { value: '194LBA', label: '194LBA - Income from units of Business Trust' },
    { value: '194LBA(a)', label: '194LBA(a) - Interest from Business Trust to Resident' },
    { value: '194LBA(b)', label: '194LBA(b) - Dividend from Business Trust to Resident' },
    { value: '194LBB', label: '194LBB - Income in respect of units of Investment Fund' },
    { value: '194LBC', label: '194LBC - Income in respect of investment in Securitization Trust' },
    { value: '194N', label: '194N - Cash Withdrawal > 1 Crore' },
    { value: '194N-FT', label: '194N-FT - Cash payment to Non-filer Co-op Societies' },
    { value: '194NC', label: '194NC - Cash payment to Co-op Societies' },
    { value: '194NF', label: '194NF - Cash payment to non-filers' },
    { value: '194O', label: '194O - TDS on E-commerce transactions' },
    { value: '194Q', label: '194Q - Purchase of Goods' },
    { value: '194R', label: '194R - Benefits or Perquisites of Business/Profession' },
    { value: '194RP', label: '194RP - Benefits or Perquisites (Kind/Cash)' },
    { value: '194S', label: '194S - Transfer of Virtual Digital Asset' },
    { value: '194SP', label: '194SP - Transfer of Virtual Digital Asset (Kind/Exchange)' }
  ]

  const sectionOptions26Q_new = [
    { value: '1004', label: '1004 - Premature EPF Withdrawal' },
    { value: '1005', label: '1005 - Insurance Commission' },
    { value: '1006', label: '1006 - Commission or Brokerage (Others)' },
    { value: '1007', label: '1007 - Rent on Land/Building (By Individuals/HUF)' },
    { value: '1009', label: '1009 - Rent on Land/Building (Audit-liable cases)' },
    { value: '1010', label: '1010 - Rent on Plant, Machinery, or Equipment' },
    { value: '1011', label: '1011 - Life Insurance Policy Maturity Payments' },
    { value: '1012', label: '1012 - Compensation on Acquisition of Immovable Property' },
    { value: '1019', label: '1019 - Interest on Securities' },
    { value: '1021', label: '1021 - Interest other than Securities (Banks/Firms)' },
    { value: '1023', label: '1023 - Payment to Contractors (Carrying out any work)' },
    { value: '1024', label: '1024 - Supply of Labour/Manpower Contractors' },
    { value: '1026', label: '1026 - Fees for Technical Services (FTS) / Film Royalty' },
    { value: '1027', label: '1027 - Professional Fees (CAs, Lawyers, Doctors, etc.)' },
    { value: '1028', label: '1028 - Director Remuneration (Non-salary cases)' },
    { value: '1029', label: '1029 - Dividend Income' },
    { value: '1033', label: '1033 - TDS on Purchase of Goods (>50 Lakhs limit)' },
    { value: '1034', label: '1034 - TDS on E-commerce Participants' },
    { value: '1035', label: '1035 - Purchase of Immovable Property' },
    { value: '1037', label: '1037 - Transfer of Virtual Digital Assets (VDA / Crypto)' },
    { value: '1058', label: '1058 - Winnings from Lottery, Crosswords, or Card Games' },
    { value: '1060', label: '1060 - Winnings from Online Games' },
    { value: '1064', label: '1064 - Cash Withdrawals exceeding statutory limits' },
    { value: '1067', label: '1067 - Salary, Interest, or Bonus Paid to Partners of a Firm' }
  ]

  const sectionCodes = (is26Q && financialYear && parseInt(financialYear.split('-')[0]) >= 2026)
    ? sectionOptions26Q_new
    : (selectedSectionOptions || defaultSectionCodes)

  const defaultMinorHeads = [
    { value: '100', label: '100 - Advance Tax' },
    { value: '200', label: '200 - TDS payable by taxpayer' },
    { value: '400', label: '400 - TDS regular assessment' }
  ]

  const minorHeads = selectedMinorHeadOptions || defaultMinorHeads

  const calculateTotalAmount = (row) => {
    return (
      parseFloat(row.tdsAmount || 0) +
      parseFloat(row.interest || 0) +
      parseFloat(row.other || 0) +
      parseFloat(row.feesAmount || 0)
    )
  }

  const getChallanUsedAmount = (challan, tdsDataList, index) => {
    if (!challan) return 0;
    const cid = challan.id;
    const cno = challan.challanSerialNo || challan.challanNo;
    const recNo = challan.recordNo;

    return (tdsDataList || [])
      .filter(r => {
        if (!r) return false;
        const matchId = (cid !== undefined && cid !== null) && (String(r.challanId) === String(cid) || String(r.challanNo) === String(cid));
        const matchRecNo = (recNo !== undefined && recNo !== null) && String(r.challanId) === String(recNo);
        const matchNo = (cno !== undefined && cno !== null) && (String(r.challanNo) === String(cno) || String(r.challanId) === String(cno));
        const matchIndex = index !== undefined && (String(r.challanId) === String(index + 1) || String(r.challanNo) === String(index + 1));
        return matchId || matchRecNo || matchNo || matchIndex;
      })
      .reduce((sum, r) => sum + (parseFloat(r.tdsAmount) || parseFloat(r.tds) || parseFloat(r.totalTaxDeposited) || parseFloat(r.totalTaxDeducted) || 0), 0)
  }

  const enrichRowData = (row, tdsDataList, index) => {
    const total = calculateTotalAmount(row)
    const deducteeUsed = getChallanUsedAmount(row, tdsDataList, index)
    const intAlloc = parseFloat(row.interestAllocation !== undefined && row.interestAllocation !== '' ? row.interestAllocation : (row.interest || 0)) || 0
    const feesAlloc = parseFloat(row.feesAllocation !== undefined && row.feesAllocation !== '' ? row.feesAllocation : (row.otherAllocation !== undefined && row.otherAllocation !== '' ? row.otherAllocation : (row.feesAmount || row.other || 0))) || 0
    const used = deducteeUsed + intAlloc + feesAlloc
    const balance = total - used
    return {
      ...row,
      interestAllocation: row.interestAllocation !== undefined && row.interestAllocation !== '' ? row.interestAllocation : (row.interest || 0),
      feesAllocation: row.feesAllocation !== undefined && row.feesAllocation !== '' ? row.feesAllocation : (row.otherAllocation !== undefined && row.otherAllocation !== '' ? row.otherAllocation : (row.feesAmount || row.other || 0)),
      totalAmount: parseFloat(total.toFixed(2)),
      usedAmount: parseFloat(used.toFixed(2)),
      balance: parseFloat(balance.toFixed(2))
    }
  }

  // Grid Handlers
  const handleGridDataChange = (newData, rowIndex, columnKey) => {
    // If specific column changed, validate input length/type immediately
    if (rowIndex !== undefined && columnKey) {
      const row = newData[rowIndex]

      // BSR Code Validation: Max 7 digits
      if (columnKey === 'bsrCode') {
        if (!/^\d*$/.test(row.bsrCode)) {
          return
        }
        if (row.bsrCode.length > 7) {
          return
        }
      }

      // Challan Serial No Validation: Max 5 digits
      if (columnKey === 'challanSerialNo') {
        if (row.challanSerialNo && row.challanSerialNo.length > 5) {
          return
        }
      }
    }

    // Recalculate totals for modified rows
    const updatedData = newData.map(row => enrichRowData(row, propTdsData || []))

    // Check for negative balance logic
    if (rowIndex !== undefined) {
      const updatedRow = updatedData[rowIndex]
      if (updatedRow.balance < 0) {
        toast.error("No Balance Available to used")
        return // Reject the change
      }
    }

    setChallanData(updatedData)
    onChallanDataChange(updatedData)
  }

  const handleGridRowAdd = () => {
    const newRecord = {
      id: Date.now(),
      sectionCode: '',
      tdsAmount: '',
      interest: '',
      other: '',
      feesAmount: '',
      interestAllocation: '',
      feesAllocation: '',
      chequeDDNo: '',
      bsrCode: '',
      dateOfDeposit: '',
      challanSerialNo: '',
      bookEntry: false,
      minorHead: '200',
      recordNo: ''
    }
    const enrichedRecord = enrichRowData(newRecord, propTdsData || [])
    const updatedData = [...challanData, enrichedRecord]
    setChallanData(updatedData)
    onChallanDataChange(updatedData)
  }

  const handleGridRowDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this row?')) {
      const updatedData = challanData.filter(row => row.id !== id)
      setChallanData(updatedData)
      onChallanDataChange(updatedData)
    }
  }



  const [filterMode, setFilterMode] = useState('all'); // 'all' | 'changed'

  const changedCount = React.useMemo(() => {
    if (!isCorrectionMode || !challanData) return 0;
    return challanData.filter((row, idx) => isRowModified(row, idx) || isRowNew(row, idx)).length;
  }, [challanData, isCorrectionMode, propOriginalChallanData]);

  const filteredData = React.useMemo(() => {
    let result = showOnlyWithBalance
      ? challanData.filter(row => row.balance > 0)
      : challanData;

    if (isCorrectionMode && filterMode === 'changed') {
      result = result.filter((row, idx) => isRowModified(row, idx) || isRowNew(row, idx));
    }
    return result;
  }, [showOnlyWithBalance, challanData, filterMode, isCorrectionMode]);

  // Calculate Challan Totals
  const totalChallanTds = (challanData || []).reduce((acc, c) => acc + (parseFloat(c.tdsAmount || 0) || 0), 0);
  const totalChallanAmount = (challanData || []).reduce((acc, c) => acc + (parseFloat(c.totalAmount || c.challanAmount || 0) || 0), 0);
  const totalUsed = (challanData || []).reduce((acc, c) => acc + (parseFloat(c.usedAmount || 0) || 0), 0);
  const totalBalance = (challanData || []).reduce((acc, c) => acc + (parseFloat(c.balance || 0) || 0), 0);

  const filteredChallanTds = (filteredData || []).reduce((acc, c) => acc + (parseFloat(c.tdsAmount || 0) || 0), 0);
  const filteredChallanAmount = (filteredData || []).reduce((acc, c) => acc + (parseFloat(c.totalAmount || c.challanAmount || 0) || 0), 0);
  const filteredUsed = (filteredData || []).reduce((acc, c) => acc + (parseFloat(c.usedAmount || 0) || 0), 0);
  const filteredBalance = (filteredData || []).reduce((acc, c) => acc + (parseFloat(c.balance || 0) || 0), 0);

  const formatCurrency = (val) => {
    return Number(val || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Challan Details</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Manage tax deposit challans, allocations, and available balances
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {isCorrectionMode && (
            <div className="flex items-center bg-gray-100 p-1 rounded-lg border border-gray-200 text-xs font-semibold mr-2">
              <button
                type="button"
                onClick={() => setFilterMode('all')}
                className={`px-3 py-1.5 rounded-md transition-colors ${filterMode === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
              >
                All Records ({challanData.length})
              </button>
              <button
                type="button"
                onClick={() => setFilterMode('changed')}
                className={`px-3 py-1.5 rounded-md transition-colors flex items-center gap-1.5 ${filterMode === 'changed' ? 'bg-amber-500 text-white shadow-sm' : 'text-amber-700 hover:bg-amber-50'}`}
              >
                <span className="w-2 h-2 rounded-full bg-amber-300"></span>
                Changed Records ({changedCount})
              </button>
            </div>
          )}
          <label className="flex items-center space-x-2 text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200 cursor-pointer">
            <input
              type="checkbox"
              checked={showOnlyWithBalance}
              onChange={(e) => setShowOnlyWithBalance(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <span className="text-xs font-medium">Show only with balance</span>
          </label>
          {!isCorrectionMode && (
            <>
              <button
                onClick={handleImportClick}
                className="flex items-center px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-md border-2 border-indigo-500 font-bold text-sm"
              >
                <Download className="w-4 h-4 mr-1.5" />
                Import Challan
              </button>
              <button
                onClick={handleGridRowAdd}
                className="flex items-center px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-md border-2 border-green-500 font-bold text-sm"
              >
                <Plus className="w-4 h-4 mr-1.5" />
                Add Row
              </button>
            </>
          )}
        </div>
      </div>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50/60 border border-blue-200/80 rounded-xl p-3.5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-blue-700">Total Challan Amount</span>
            <span className="p-1 bg-blue-100 text-blue-700 rounded-md text-xs font-bold flex items-center justify-center">
              <IndianRupee className="w-3.5 h-3.5" />
            </span>
          </div>
          <div className="mt-2 text-xl font-bold text-blue-900">
            ₹{formatCurrency(totalChallanAmount)}
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5">Across {challanData.length} challans</div>
        </div>

        <div className="bg-gradient-to-br from-emerald-50 to-teal-50/60 border border-emerald-200/80 rounded-xl p-3.5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Total TDS In Challans</span>
            <span className="p-1 bg-emerald-100 text-emerald-700 rounded-md text-xs font-bold flex items-center justify-center">
              <IndianRupee className="w-3.5 h-3.5" />
            </span>
          </div>
          <div className="mt-2 text-xl font-bold text-emerald-900">
            ₹{formatCurrency(totalChallanTds)}
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5">Tax deposited amount</div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-violet-50/60 border border-purple-200/80 rounded-xl p-3.5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-purple-700">Total Allocated / Used</span>
            <span className="p-1 bg-purple-100 text-purple-700 rounded-md text-xs font-bold flex items-center justify-center">
              <Layers className="w-3.5 h-3.5" />
            </span>
          </div>
          <div className="mt-2 text-xl font-bold text-purple-900">
            ₹{formatCurrency(totalUsed)}
          </div>
          <div className="text-[11px] text-purple-700 mt-0.5">Utilized against deductees</div>
        </div>

        <div className="bg-gradient-to-br from-amber-50 to-emerald-50/60 border border-amber-200/80 rounded-xl p-3.5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-amber-800">Remaining Balance</span>
            <span className="p-1 bg-emerald-100 text-emerald-700 rounded-md text-xs font-bold flex items-center justify-center">
              <IndianRupee className="w-3.5 h-3.5" />
            </span>
          </div>
          <div className="mt-2 text-xl font-bold text-emerald-900">
            ₹{formatCurrency(totalBalance)}
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5">Available for further allocation</div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <ExcelDataGrid
          columns={[
            { key: 'sectionCode', label: 'Section', type: 'select', width: '250px', options: sectionCodes, required: false, readOnly: isCorrectionMode, disabled: isCorrectionMode },
            { key: 'bsrCode', label: 'BSR Code', type: 'text', width: '100px', required: true, readOnly: isCorrectionMode, disabled: isCorrectionMode },
            { key: 'dateOfDeposit', label: 'Date Deposit', type: 'date', width: '120px', placeholder: 'DD-MM-YYYY', required: true, readOnly: isCorrectionMode, disabled: isCorrectionMode },
            { key: 'minorHead', label: 'Minor Head', type: 'select', width: '250px', options: minorHeads, required: true, readOnly: isCorrectionMode, disabled: isCorrectionMode },
            { key: 'challanSerialNo', label: 'Serial No', type: 'text', width: '120px', required: true, readOnly: isCorrectionMode, disabled: isCorrectionMode },
            { key: 'tdsAmount', label: 'TDS (Rs.)', type: 'number', width: '100px', readOnly: isCorrectionMode, disabled: isCorrectionMode },
            { key: 'interest', label: 'Interest', type: 'number', width: '100px', readOnly: isCorrectionMode, disabled: isCorrectionMode },
            { key: 'other', label: 'Other', type: 'number', width: '100px', readOnly: isCorrectionMode, disabled: isCorrectionMode },
            { key: 'feesAmount', label: 'Fees', type: 'number', width: '100px', readOnly: isCorrectionMode, disabled: isCorrectionMode },
            { key: 'totalAmount', label: 'Total', type: 'number', width: '100px', readOnly: true },
            { key: 'interestAllocation', label: 'Interest Allocation', type: 'number', width: '140px' },
            { key: 'feesAllocation', label: 'Fees Allocation', type: 'number', width: '140px' },
            { key: 'usedAmount', label: 'Used', type: 'number', width: '100px', readOnly: true },
            { key: 'balance', label: 'Balance', type: 'number', width: '100px', readOnly: true }
          ]}
          data={filteredData}
          onDataChange={handleGridDataChange}
          onRowAdd={isCorrectionMode ? undefined : handleGridRowAdd}
          onRowDelete={isCorrectionMode ? undefined : handleGridRowDelete}
          isRowDeleteDisabled={() => isCorrectionMode}
          height="calc(100vh - 460px)"
          stickyFirstColumn={true}
          getRowClassName={getRowClassName}
          summaryRow={filteredData.length > 0 ? {
            sectionCode: <span className="font-bold text-gray-800">TOTAL:</span>,
            tdsAmount: <span className="font-bold text-emerald-700">₹{formatCurrency(filteredChallanTds)}</span>,
            totalAmount: <span className="font-bold text-blue-700">₹{formatCurrency(filteredChallanAmount)}</span>,
            usedAmount: <span className="font-bold text-purple-700">₹{formatCurrency(filteredUsed)}</span>,
            balance: <span className="font-bold text-emerald-700">₹{formatCurrency(filteredBalance)}</span>
          } : null}
        />

        {challanData.length === 0 && (
          <div className="p-4 text-center text-gray-500 text-sm">
            No Challan details added yet. Use the grid above or "Upload Excel" to add data.
          </div>
        )}
      </div>




      {/* Import Challan Modal */}
      {
        showImportModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
            <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl border border-gray-100">
              <div className="flex justify-between items-center mb-4 border-b pb-2">
                <h3 className="text-xl font-bold text-gray-800">Import Challan Details</h3>
                <button onClick={() => setShowImportModal(false)} className="text-gray-500 hover:text-gray-700">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleImportSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tax Deduction Account Number (TAN)</label>
                  <input type="text" value={tan} disabled className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      type={showImportPassword ? "text" : "password"}
                      value={importPassword}
                      onChange={(e) => setImportPassword(e.target.value)}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
                      placeholder="Enter Income Tax Portal Password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowImportPassword(!showImportPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                    >
                      {showImportPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">From Date <span className="text-red-500">*</span></label>
                    <input
                      type="date"
                      value={importDateRange.from}
                      onChange={(e) => setImportDateRange({ ...importDateRange, from: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">To Date <span className="text-red-500">*</span></label>
                    <input
                      type="date"
                      value={importDateRange.to}
                      onChange={(e) => setImportDateRange({ ...importDateRange, to: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
                      required
                    />
                  </div>
                </div>

                <div className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    id="headlessMode"
                    checked={isHeadless}
                    onChange={(e) => setIsHeadless(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="headlessMode" className="ml-2 block text-sm text-gray-700">
                    Run in Background (Headless Mode)
                  </label>
                </div>

                {importStatus && (
                  <div className="mb-4 p-3 bg-blue-50 text-blue-700 rounded-md text-sm font-medium animate-pulse">
                    Status: {importStatus}
                  </div>
                )}

                <div className="pt-4 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowImportModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                    disabled={isImporting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 min-w-[100px]"
                    disabled={isImporting}
                    style={{ visibility: 'visible !important', display: 'flex' }}
                  >
                    {isImporting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        Importing...
                      </>
                    ) : (
                      'Import'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

    </div >
  )
}

export default ChallanDetailsTable
